import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '../../hooks/useAuth0';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import api from '../../services/api';

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  'pk_test_51T4mev1nJiKZyVSiLZomW2ZIlG7tGic3a9EV31Vt7sQfUa3C6ucPg4vRaJAQiHEK6kM0GGzHcfT74NEqvrjo4Z2g00gmZaDZhu'
);

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --navy:#0D2B45; --blue:#1B6CA8; --coral:#F4845F;
    --gray:#6B7280; --light:#F1F5F9; --border:#E5E7EB; --text:#1A2332;
    --green:#137333; --red:#DC2626; --purple:#1A5EA8;
  }
  body { font-family:'DM Sans',sans-serif; background:var(--light); color:var(--text); }
  .pp-page { max-width:960px; margin:0 auto; padding:32px 24px 80px; display:grid; grid-template-columns:1fr 340px; gap:24px; align-items:start; }
  .pp-back-link { display:flex; align-items:center; gap:6px; font-size:13px; color:var(--gray); cursor:pointer; margin-bottom:20px; transition:color 0.2s; background:none; border:none; font-family:'DM Sans',sans-serif; }
  .pp-back-link:hover { color:var(--blue); }
  .pp-card { background:white; border-radius:16px; border:1px solid var(--border); overflow:hidden; margin-bottom:16px; }
  .pp-card-header { padding:18px 24px; border-bottom:1px solid var(--border); display:flex; align-items:center; gap:12px; }
  .pp-card-icon { width:36px; height:36px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:18px; background:#EBF5FF; }
  .pp-card-title { font-family:'Playfair Display',serif; font-size:17px; font-weight:600; color:var(--navy); }
  .pp-card-subtitle { font-size:12px; color:var(--gray); margin-top:2px; }
  .pp-card-body { padding:24px; }
  .stripe-element-wrap { border:1.5px solid var(--border); border-radius:8px; padding:14px 16px; background:white; min-height:44px; transition:border-color 0.2s; margin-bottom:16px; }
  .stripe-element-wrap:focus-within { border-color:var(--blue); box-shadow:0 0 0 3px rgba(27,108,168,0.08); }
  .security-strip { display:flex; align-items:center; justify-content:center; gap:20px; padding:14px 24px; background:var(--light); border-top:1px solid var(--border); flex-wrap:wrap; }
  .security-item { display:flex; align-items:center; gap:5px; font-size:12px; color:var(--gray); font-weight:500; }
  .pp-submit-wrap { padding:20px 24px; border-top:1px solid var(--border); }
  .pp-submit-btn { width:100%; padding:16px; border:none; border-radius:10px; font-family:'DM Sans',sans-serif; font-size:16px; font-weight:700; color:white; background:linear-gradient(135deg,var(--coral),#E86A3F); cursor:pointer; transition:all 0.2s; box-shadow:0 4px 16px rgba(244,132,95,0.3); display:flex; align-items:center; justify-content:center; gap:8px; }
  .pp-submit-btn:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 8px 24px rgba(244,132,95,0.4); }
  .pp-submit-btn:disabled { background:#9CA3AF; cursor:not-allowed; transform:none; box-shadow:none; }
  .pp-submit-note { text-align:center; font-size:12px; color:var(--gray); margin-top:10px; }
  .pp-error { background:#FEF2F2; border:1px solid #FECACA; border-radius:10px; padding:14px 18px; color:var(--red); font-size:13px; margin-bottom:16px; display:flex; align-items:flex-start; gap:10px; }
  .pp-processing { position:fixed; inset:0; background:rgba(13,43,69,0.85); z-index:500; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:20px; }
  .pp-spinner { width:52px; height:52px; border:4px solid rgba(255,255,255,0.2); border-top-color:var(--coral); border-radius:50%; animation:pp-spin 0.8s linear infinite; }
  @keyframes pp-spin { to { transform:rotate(360deg); } }
  .pp-processing-text { font-family:'Playfair Display',serif; font-size:22px; color:white; font-weight:600; }
  .pp-processing-sub { font-size:14px; color:rgba(255,255,255,0.6); }
  .pp-summary { background:white; border:1px solid var(--border); border-radius:16px; overflow:hidden; position:sticky; top:90px; }
  .pp-summary-header { background:var(--navy); padding:20px 24px; color:white; }
  .pp-summary-title { font-family:'Playfair Display',serif; font-size:18px; font-weight:700; margin-bottom:4px; }
  .pp-summary-subtitle { font-size:12px; color:rgba(255,255,255,0.6); }
  .pp-line-item { display:flex; justify-content:space-between; align-items:center; padding:14px 24px; border-bottom:1px solid var(--light); font-size:14px; }
  .pp-line-label { color:var(--gray); display:flex; align-items:center; gap:8px; }
  .pp-line-value { font-weight:600; color:var(--text); }
  .pp-subtotal-row { display:flex; justify-content:space-between; padding:8px 24px; font-size:13px; color:var(--gray); border-bottom:1px solid var(--light); }
  .pp-total { display:flex; justify-content:space-between; align-items:center; padding:16px 24px; background:var(--light); border-top:2px solid var(--border); }
  .pp-total-label { font-size:14px; font-weight:700; color:var(--navy); }
  .pp-total-value { font-size:28px; font-weight:700; color:var(--purple); }
  .pp-included { display:grid; grid-template-columns:repeat(3,1fr); gap:12px; margin-bottom:0; }
  .pp-included-item { text-align:center; padding:16px 12px; border-radius:10px; border:1px solid var(--border); }
  .pp-included-item.has { background:#f0fdf4; border-color:#bbf7d0; }
  .pp-included-item.none { background:var(--light); opacity:0.6; }
  .pp-included-icon { font-size:22px; margin-bottom:6px; }
  .pp-included-label { font-size:11px; color:var(--gray); font-weight:600; text-transform:uppercase; letter-spacing:0.04em; }
  .pp-included-value { font-size:11px; color:var(--green); font-weight:500; margin-top:3px; }
  .pp-included-item.none .pp-included-value { color:var(--gray); }
  .pp-success { max-width:600px; margin:80px auto; padding:0 24px; text-align:center; }
  .pp-success-icon { font-size:80px; margin-bottom:24px; }
  .pp-success-title { font-family:'Playfair Display',serif; font-size:32px; font-weight:700; color:var(--green); margin-bottom:12px; }
  .pp-success-ref { font-size:16px; color:var(--gray); margin-bottom:8px; }
  .pp-success-msg { font-size:14px; color:var(--gray); line-height:1.6; }
  .pp-loading { display:flex; align-items:center; justify-content:center; padding:80px 24px; gap:14px; color:var(--gray); font-size:14px; }
  .pp-loading-spinner { width:28px; height:28px; border:3px solid var(--border); border-top-color:var(--purple); border-radius:50%; animation:pp-spin 0.8s linear infinite; }
  @media (max-width:700px) { .pp-page { grid-template-columns:1fr; } .pp-summary { position:static; } }
`;

// ── Checkout Form ─────────────────────────────────────────────────────────────
function CheckoutForm({ booking, onSuccess, user , patientEmail }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setProcessing(true);
    setError(null);

    const { error: stripeErr, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });

    if (stripeErr) {
      setError(stripeErr.message);
      setProcessing(false);
      return;
    }

    if (!paymentIntent || paymentIntent.status !== 'succeeded') {
      setError('Payment was not completed. Please try again.');
      setProcessing(false);
      return;
    }

    try {
      const response = await api.post(
        `/v1/travel/bookings/${booking.id}/confirm-payment`,
        { paymentIntentId: paymentIntent.id,
          patientEmail: patientEmail,
         }
      );
      if (response.data.success) {
        onSuccess();
      } else {
        setError(response.data.error || 'Payment confirmation failed');
        setProcessing(false);
      }
    } catch (err) {
      setError(
        'Payment succeeded but booking update failed. Contact support with ref: ' +
        paymentIntent.id
      );
      setProcessing(false);
    }
  };

  return (
    <>
      {processing && (
        <div className="pp-processing">
          <div className="pp-spinner" />
          <div className="pp-processing-text">Processing Payment…</div>
          <div className="pp-processing-sub">Please don't close this window</div>
        </div>
      )}
      <div className="pp-card">
        <div className="pp-card-header">
          <div className="pp-card-icon">💳</div>
          <div>
            <div className="pp-card-title">Payment Method</div>
            <div className="pp-card-subtitle">All transactions are secured and encrypted</div>
          </div>
        </div>
        <div className="pp-card-body">
          {error && <div className="pp-error"><span>⚠</span><span>{error}</span></div>}
          <div className="stripe-element-wrap">
            <PaymentElement
              options={{
                layout: 'tabs',
                paymentMethodOrder: ['card', 'apple_pay', 'google_pay'],
              }}
            />
          </div>
        </div>
        <div className="security-strip">
          <span className="security-item">🔒 256-bit SSL</span>
          <span className="security-item">🛡 PCI DSS Compliant</span>
          <span className="security-item">✓ Powered by Stripe</span>
          <span className="security-item">🏥 HIPAA Safe</span>
        </div>
      </div>
      <div className="pp-card" style={{ marginBottom: 0 }}>
        <div className="pp-submit-wrap">
          <button
            className="pp-submit-btn"
            onClick={handlePay}
            disabled={processing || !stripe || !elements}
          >
            {processing ? (
              <>
                <div style={{ width: 18, height: 18, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: 'white', borderRadius: '50%', animation: 'pp-spin 0.8s linear infinite' }} />
                Processing…
              </>
            ) : (
              <>🔒 Pay ${booking.totalAmount?.toFixed(2)}</>
            )}
          </button>
          <div className="pp-submit-note">🔒 Your payment is protected by Stripe</div>
        </div>
      </div>
    </>
  );
}

// ── Main PaymentPage ──────────────────────────────────────────────────────────
export default function PaymentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loginWithRedirect, user } = useAuth0();

  const [booking, setBooking] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initError, setInitError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [patientEmail, setPatientEmail] = useState(null);
  

  useEffect(() => {
    if (!isAuthenticated) {
      loginWithRedirect({ appState: { returnTo: `/travel/payment/${id}` } });
    } else {
      initPage();
    }
  }, [id, isAuthenticated]);

  const initPage = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/v1/travel/bookings/${id}`);
      const b = res.data;

      if (b.paymentStatus === 'CAPTURED') {
        navigate(`/travel/bookings/${id}`);
        return;
      }

      setBooking(b);
      const patientRes = await api.get('/v1/patients/me');
      const patientEmail = patientRes.data.email;
      setPatientEmail(patientEmail); 

      const intentRes = await api.post(
        `/v1/travel/bookings/${id}/create-payment-intent`
      );
      setClientSecret(intentRes.data.clientSecret);
    } catch (err) {
      setInitError(err.response?.data?.error || 'Failed to initialize payment');
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = () => {
    setSuccess(true);
    setTimeout(() => navigate(`/travel/bookings/${id}`), 4000);
  };

  const getTierColor = (t) =>
    t === 'CONCIERGE' ? '#10b981' : t === 'WHITE_GLOVE' ? '#f59e0b' : '#1A5EA8';

  const appearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: '#1A5EA8',
      colorBackground: '#ffffff',
      colorText: '#1A2332',
      colorDanger: '#DC2626',
      fontFamily: "'DM Sans', sans-serif",
      borderRadius: '8px',
      fontSizeBase: '14px',
    },
  };

  const elementsOptions = useMemo(
    () => (clientSecret ? { clientSecret, appearance } : null),
    [clientSecret]
  );

  const lineItems = booking
    ? [
        { icon: '🎫', label: 'Service Fee', amount: booking.serviceTierPrice, show: true },
        { icon: '✈️', label: `Flight (${booking.flightDepartureAirport || ''} → ${booking.flightArrivalAirport || ''})`, amount: booking.flightAmount, show: !!booking.flightAmount },
        { icon: '🏨', label: booking.hotelName || 'Hotel', amount: booking.hotelAmount, show: !!booking.hotelAmount },
        { icon: '🚗', label: booking.groundTransportType || 'Car Rental', amount: booking.groundTransportAmount, show: !!booking.groundTransportAmount },
      ].filter((i) => i.show)
    : [];

  if (success) {
    return (
      <>
        <style>{css}</style>
        <div className="pp-success">
          <div className="pp-success-icon">✅</div>
          <div className="pp-success-title">Payment Confirmed!</div>
          <div className="pp-success-ref">Booking Reference: <strong>{booking?.bookingReference}</strong></div>
          <div className="pp-success-msg">
            A confirmation email has been sent to you. Your travel coordinator will be in touch shortly.
            <br /><br />Redirecting to your booking…
          </div>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <style>{css}</style>
        <div className="pp-loading">
          <div className="pp-loading-spinner" />
          Initializing secure payment…
        </div>
      </>
    );
  }

  if (initError) {
    return (
      <>
        <style>{css}</style>
        <div style={{ maxWidth: 600, margin: '80px auto', padding: '0 24px' }}>
          <div className="pp-error">⚠ {initError}</div>
          <button className="pp-back-link" onClick={() => navigate(`/travel/bookings/${id}`)}>
            ← Back to Booking
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{css}</style>
      <div className="pp-page">

        {/* LEFT */}
        <div>
          <button className="pp-back-link" onClick={() => navigate(`/travel/bookings/${id}`)}>
            ← Back to Booking
          </button>

          {/* What's included */}
          <div className="pp-card">
            <div className="pp-card-header">
              <div className="pp-card-icon">📋</div>
              <div>
                <div className="pp-card-title">What's Included</div>
                <div className="pp-card-subtitle">{booking?.trialName}</div>
              </div>
            </div>
            <div className="pp-card-body">
              <div className="pp-included">
                <div className={`pp-included-item ${booking?.flightBookingId ? 'has' : 'none'}`}>
                  <div className="pp-included-icon">✈️</div>
                  <div className="pp-included-label">Flight</div>
                  <div className="pp-included-value">
                    {booking?.flightBookingId
                      ? `${booking.flightDepartureAirport} → ${booking.flightArrivalAirport}`
                      : 'Not selected'}
                  </div>
                </div>
                <div className={`pp-included-item ${booking?.hotelBookingId ? 'has' : 'none'}`}>
                  <div className="pp-included-icon">🏨</div>
                  <div className="pp-included-label">Hotel</div>
                  <div className="pp-included-value">
                    {booking?.hotelBookingId ? booking.hotelName : 'Not selected'}
                  </div>
                </div>
                <div className={`pp-included-item ${booking?.groundTransportType ? 'has' : 'none'}`}>
                  <div className="pp-included-icon">🚗</div>
                  <div className="pp-included-label">Car Rental</div>
                  <div className="pp-included-value">
                    {booking?.groundTransportType || 'Not selected'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stripe Payment */}
          {clientSecret && elementsOptions && (
            <Elements stripe={stripePromise} options={elementsOptions}>
              <CheckoutForm booking={booking} onSuccess={handleSuccess}  user= {user} patientEmail={patientEmail}/>
            </Elements>
          )}
        </div>

        {/* RIGHT — Order Summary */}
        <div className="pp-summary">
          <div className="pp-summary-header">
            <div className="pp-summary-title">Order Summary</div>
            <div className="pp-summary-subtitle">Ref: {booking?.bookingReference}</div>
          </div>
          {lineItems.map(({ icon, label, amount }) => (
            <div key={label} className="pp-line-item">
              <span className="pp-line-label">{icon} {label}</span>
              <span className="pp-line-value">${amount?.toFixed(2)}</span>
            </div>
          ))}
          <div className="pp-subtotal-row">
            <span>Subtotal</span>
            <span>${booking?.subtotalAmount?.toFixed(2)}</span>
          </div>
          <div className="pp-total">
            <span className="pp-total-label">Total</span>
            <span className="pp-total-value" style={{ color: getTierColor(booking?.serviceTier) }}>
              ${booking?.totalAmount?.toFixed(2)}
            </span>
          </div>
        </div>

      </div>
    </>
  );
}